/**
 * Saved Configs Service
 * 
 * Centralized service for managing saved configurations using MySQL database
 * Replaces localStorage-based config storage with server-side persistence
 */

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://nlfx-cable.luknai.com/api'
  : 'http://localhost:3000/api';

class SavedConfigsService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Generate a unique config ID
   * @param {string} name - Configuration name
   * @returns {string} - Unique config ID
   */
  generateConfigId(name) {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    const nameHash = btoa(name).replace(/[^a-zA-Z0-9]/g, '').substring(0, 8);
    return `${nameHash}${random}${timestamp.toString(36)}`.substring(0, 16);
  }

  /**
   * Get current user ID (from localStorage or generate anonymous)
   * @returns {string} - User ID
   */
  getCurrentUserId() {
    try {
      const currentUser = localStorage.getItem('currentUser');
      if (currentUser) {
        const user = JSON.parse(currentUser);
        return user.username || user.id || 'anonymous';
      }
    } catch (error) {
      console.warn('Error parsing current user:', error);
    }
    return 'anonymous';
  }

  /**
   * Save a configuration to the database
   * @param {Object} config - Configuration object
   * @param {string} name - Configuration name
   * @param {boolean} isPublic - Whether config is public
   * @param {number} expiresInDays - Days until expiration (optional)
   * @returns {Promise<Object>} - Save result
   */
  async saveConfig(config, name, isPublic = false, expiresInDays = null) {
    try {
      const configId = this.generateConfigId(name);
      const createdBy = this.getCurrentUserId();
      
      // Determine cable type from config
      const cableType = this.determineCableType(config);
      
      // Calculate expiration date if specified
      const expiresAt = expiresInDays ? new Date(Date.now() + expiresInDays * 24 * 60 * 60 * 1000) : null;
      
      const response = await fetch(`${API_BASE_URL}/saved-configs?action=save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          configId,
          configName: name,
          configData: config,
          cableType,
          createdBy,
          isPublic,
          expiresAt: expiresAt ? expiresAt.toISOString() : null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save configuration');
      }

      const result = await response.json();
      
      // Cache the config
      this.cache.set(configId, {
        config,
        timestamp: Date.now()
      });

      console.log('✅ Configuration saved successfully:', configId);
      return {
        success: true,
        configId: result.configId,
        url: `${window.location.origin}/?config=${result.configId}`
      };
    } catch (error) {
      console.error('❌ Error saving configuration:', error);
      throw error;
    }
  }

  /**
   * Load a configuration from the database
   * @param {string} configId - Configuration ID
   * @returns {Promise<Object>} - Configuration object
   */
  async loadConfig(configId) {
    try {
      // Check cache first
      const cached = this.cache.get(configId);
      if (cached && (Date.now() - cached.timestamp) < this.cacheTimeout) {
        console.log('✅ Configuration loaded from cache:', configId);
        return cached.config;
      }

      const response = await fetch(`${API_BASE_URL}/saved-configs?action=load&configId=${configId}`);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Configuration not found');
        }
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load configuration');
      }

      const result = await response.json();
      
      // Cache the config
      this.cache.set(configId, {
        config: result.config.data,
        timestamp: Date.now()
      });

      console.log('✅ Configuration loaded successfully:', configId);
      return result.config.data;
    } catch (error) {
      console.error('❌ Error loading configuration:', error);
      throw error;
    }
  }

  /**
   * Get user's saved configurations
   * @param {string} userId - User ID (optional, defaults to current user)
   * @returns {Promise<Array>} - Array of configuration summaries
   */
  async getUserConfigs(userId = null) {
    try {
      const targetUserId = userId || this.getCurrentUserId();
      
      const response = await fetch(`${API_BASE_URL}/saved-configs?action=user-configs&userId=${targetUserId}`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get user configurations');
      }

      const result = await response.json();
      console.log(`✅ Retrieved ${result.configs.length} user configurations`);
      return result.configs;
    } catch (error) {
      console.error('❌ Error getting user configurations:', error);
      throw error;
    }
  }

  /**
   * Get public configurations
   * @returns {Promise<Array>} - Array of public configuration summaries
   */
  async getPublicConfigs() {
    try {
      const response = await fetch(`${API_BASE_URL}/saved-configs?action=public-configs`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get public configurations');
      }

      const result = await response.json();
      console.log(`✅ Retrieved ${result.configs.length} public configurations`);
      return result.configs;
    } catch (error) {
      console.error('❌ Error getting public configurations:', error);
      throw error;
    }
  }

  /**
   * Delete a configuration
   * @param {string} configId - Configuration ID
   * @returns {Promise<Object>} - Delete result
   */
  async deleteConfig(configId) {
    try {
      const createdBy = this.getCurrentUserId();
      
      const response = await fetch(`${API_BASE_URL}/saved-configs?action=delete&configId=${configId}&createdBy=${createdBy}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete configuration');
      }

      // Remove from cache
      this.cache.delete(configId);

      console.log('✅ Configuration deleted successfully:', configId);
      return { success: true };
    } catch (error) {
      console.error('❌ Error deleting configuration:', error);
      throw error;
    }
  }

  /**
   * Determine cable type from configuration
   * @param {Object} config - Configuration object
   * @returns {string} - Cable type
   */
  determineCableType(config) {
    if (!config) return 'base';
    
    // Check for specific cable styles first
    if (config.style === 'hybrid') {
      return 'hybrid';
    } else if (config.style === 'wye') {
      return 'wye';
    } else if (config.style === 'zip' || config.style === 'zip-audio-audio') {
      return 'zip';
    } else if (config.style === 'hybrid-power-audio-audio') {
      return 'hybrid-power-audio-audio';
    }
    
    // Check for connector structure to determine type
    if (config.hybridConnectors && Object.keys(config.hybridConnectors.connectors || {}).length > 0) {
      // Check if it's a power+audio hybrid by looking for power connectors
      const connectors = config.hybridConnectors.connectors;
      const hasPowerConnector = Object.values(connectors).some(conn => 
        conn.purpose && conn.purpose.includes('power')
      );
      
      if (hasPowerConnector) {
        return 'hybrid-power-audio-audio';
      } else {
        return 'hybrid';
      }
    }
    
    // Check for wye-specific structure
    if (config.connectors && Object.keys(config.connectors).length > 2) {
      // More than 2 connectors suggests a wye configuration
      return 'wye';
    }
    
    // Default to base/standard cable
    return 'base';
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
    console.log('✅ Configuration cache cleared');
  }

  /**
   * Get cache statistics
   * @returns {Object} - Cache statistics
   */
  getCacheStats() {
    return {
      size: this.cache.size,
      timeout: this.cacheTimeout,
      entries: Array.from(this.cache.keys())
    };
  }
}

// Create singleton instance
const savedConfigsService = new SavedConfigsService();

export default savedConfigsService;
